import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import {
  ActivatedRoute,
  Router,
  RouterModule,
  NavigationEnd,
  ActivatedRouteSnapshot,
} from '@angular/router';
import { filter } from 'rxjs';
import { Dialog } from '@openng/optimus-ui/dialog';
import { DIALOG_CONTEXT, DialogContext } from '@vasta/frontend-ui-layout';

@Component({
  selector: 'ui-dialog-outlet',
  imports: [RouterModule, Dialog],
  templateUrl: './ui-dialog-outlet.component.html',
  providers: [
    {
      provide: DIALOG_CONTEXT,
      useFactory: (): DialogContext => {
        const component = inject(UiDialogOutletComponent);
        return { close: () => component.close() };
      },
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UiDialogOutletComponent implements OnInit {
  private readonly router = inject(Router);

  readonly isVisible = signal(false);

  ngOnInit(): void {
    this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe(() => this.syncFromRouterState());
  }

  close(): void {
    this.isVisible.set(false);
    this.router.navigate([{ outlets: { dialog: null } }], {
      relativeTo: this.findDialogParent(this.router.routerState.root),
      skipLocationChange: true,
    });
  }

  onHide(): void {
    this.close();
  }

  openDialog(): void {
    this.isVisible.set(true);
  }

  private syncFromRouterState(): void {
    this.isVisible.set(
      this.hasDialogOutlet(this.router.routerState.snapshot.root),
    );
  }

  private findDialogParent(route: ActivatedRoute): ActivatedRoute | null {
    if (route.snapshot.children.some((child) => child.outlet === 'dialog')) {
      return route;
    }
    for (const child of route.children) {
      const found = this.findDialogParent(child);
      if (found) return found;
    }
    return null;
  }

  private hasDialogOutlet(route: ActivatedRouteSnapshot): boolean {
    if (route.outlet === 'dialog') return true;
    return route.children.some((child) => this.hasDialogOutlet(child));
  }
}
